import { useEffect, useState } from 'react';
import { Search, Edit, Trash2, Plus } from 'lucide-react';

import { Button } from '../../../components/ui/button.tsx';
import { Input } from '../../../components/ui/input.tsx';
import { Card, CardContent } from '../../../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog.tsx';
import { Label } from '../../../components/ui/label.tsx';
import { Checkbox } from '../../../components/ui/checkbox.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.tsx';
import { LoadingSpinner } from '../../../components/ui/loading-spinner.tsx';

import { useGetAllPermissions } from '../../../api/permissions/permissionsQueries.js';
import {
  useCreateRole,
  useDeleteRolebyId,
  useGetAllRoles,
  useUpdateRolebyId,
} from '../../../api/roles/rolesQueries.js';

export default function RolesManagement() {
  // const [roles, setRoles] = useState(initialRoles);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    permissions: [],
    baseRole: '',
  });
  const [filteredRoles, setFilteredRoles] = useState([]);

  const { data: permissions, isLoading, isError } = useGetAllPermissions();
  const {
    data: roles,
    isLoading: isRolesLoading,
    isError: isRolesError,
    isSuccess: isRolesSuccess,
  } = useGetAllRoles();
  const { mutate: createRole } = useCreateRole();
  const { mutate: updateRole } = useUpdateRolebyId();
  const { mutate: deleteRole } = useDeleteRolebyId();

  useEffect(() => {
    if (!isRolesLoading && isRolesSuccess) {
      setFilteredRoles(
        roles.data.filter((role) =>
          role.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [isRolesSuccess, searchTerm, roles, isRolesLoading]);

  const handleCreateRole = () => {
    setFormData({ name: '', permissions: [] });
    setIsCreateModalOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      permissions: [...role.permissionIds],
      baseRole: role.baseRole,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setIsDeleteModalOpen(true);
  };

  const handleSaveRole = () => {
    if (selectedRole) {
      // Edit Role
      const newEditRoleData = {
        name: formData.name,
        baseRole: formData.baseRole,
        permissions: formData.permissions.map((perm) => ({ id: perm })),
      };
      updateRole([selectedRole.id, newEditRoleData]);
    } else {
      // Create new role
      const newRole = {
        name: formData.name,
        baseRole: formData.baseRole,
        permissions: formData.permissions.map((perm) => ({ id: perm })),
      };
      createRole(newRole);
    }
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedRole(null);
  };

  const handleConfirmDelete = () => {
    if (selectedRole) {
      deleteRole(selectedRole.id);
    }
    setIsDeleteModalOpen(false);
    setSelectedRole(null);
  };

  const handlePermissionChange = (permissionId, checked) => {
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        permissions: [...prev.permissions, permissionId],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((id) => id !== permissionId),
      }));
    }
  };

  const getPermissionName = (permissionId) => {
    if (isLoading) {
      return permissionId;
    } else {
      return (
        permissions.data.find((p) => p.id === permissionId)?.name ||
        permissionId
      );
    }
  };

  return (
    <div className="space-y-6 bg-white p-6 rounded-lg">
      <div className="flex items-center gap-x-4">
        <h1 className="text-lg font-semibold text-foreground">
          Role Management
        </h1>
        {/* Search Bar */}
        <div className="relative max-w-md flex-1 ">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by Role Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-purple-50 text-muted-foreground focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      </div>

      {/* Role Table */}
      <Card>
        <CardContent className="p-0">
          <div className="space-y-0">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 p-4 border-b bg-muted/30 text-sm font-medium text-muted-foreground">
              <div className="col-span-6">ROLE</div>
              <div className="col-span-4">TYPE</div>
              <div className="col-span-2 text-right">ACTIONS</div>
            </div>

            {/* Role Rows */}
            {isRolesError ? (
              <p>There was an error fetching the roles</p>
            ) : (
              <>
                {isRolesLoading ? (
                  <div className="flex place-content-center">
                    <LoadingSpinner className="m-5" />
                  </div>
                ) : (
                  <div>
                    {filteredRoles.map((role) => (
                      <div
                        key={role.id}
                        className="grid grid-cols-12 gap-4 p-4 border-b last:border-b-0 hover:bg-muted/20 text-sm"
                      >
                        <div className="col-span-6 flex items-center space-x-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              role.baseRole === 'admin'
                                ? 'bg-blue-500'
                                : role.baseRole === 'instructor'
                                ? 'bg-green-500'
                                : 'bg-gray-500'
                            }`}
                          />
                          <span className="font-medium capitalize">
                            {role.name}
                          </span>
                        </div>
                        <div className="col-span-4 flex items-center">
                          <Badge
                            variant={
                              role.name === role.baseRole
                                ? 'secondary'
                                : 'outline'
                            }
                          >
                            {role.name === role.baseRole ? 'System' : 'Custom'}
                          </Badge>
                        </div>
                        <div className="col-span-2 flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditRole(role)}
                            className="h-8 w-8 p-0 text-purple-600 hover:text-purple-600 hover:bg-purple-600/10"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {role.name !== role.baseRole && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRole(role)}
                              className="h-8 w-8 p-0 text-purple-600 hover:text-purple-600 hover:bg-purple-600/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create New Role Button */}
      {isRolesError || isRolesLoading ? (
        <></>
      ) : (
        <Button
          onClick={handleCreateRole}
          variant="outline"
          className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white hover:border-purple-600 bg-transparent"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Role
        </Button>
      )}

      {/* Create Role Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="roleName">Role Name</Label>
              <Input
                id="roleName"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter role name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseRole">Role Base Type</Label>
              <Select
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, baseRole: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role base" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="instructor">Instructor</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-4">
              <Label>Permissions</Label>
              {isError ? (
                <p>An error occured fetching permissions</p>
              ) : (
                <div>
                  {isLoading ? (
                    <div className="flex place-content-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto">
                      {permissions.data.map((permission) => (
                        <div
                          key={permission.id}
                          className="flex items-start space-x-3 p-3 border rounded-lg"
                        >
                          <Checkbox
                            id={permission.id}
                            checked={formData.permissions.includes(
                              permission.id
                            )}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(permission.id, checked)
                            }
                          />
                          <div className="space-y-1">
                            <Label
                              htmlFor={permission.id}
                              className="font-medium cursor-pointer capitalize"
                            >
                              {permission.name}
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              {permission.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRole}
              disabled={!formData.name.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">
              Edit Role: {selectedRole?.name}
            </DialogTitle>
            <div>
              {selectedRole?.name === selectedRole?.baseRole ? (
                <span className="bg-red-100 text-red-800 my-4 px-4 py-1 rounded-full">
                  You cannot edit system roles
                </span>
              ) : (
                <></>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="editRoleName">Role Name</Label>
              <Input
                id="editRoleName"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter role name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseRole">Role Base Type</Label>
              <Select
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, baseRole: value }))
                }
                defaultValue={selectedRole?.baseRole}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role base" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="instructor">Instructor</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-4">
              <Label>Current Permissions</Label>
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.permissions.map((permissionId) => (
                  <Badge
                    key={permissionId}
                    variant="secondary"
                    className="flex items-center gap-1 bg-purple-600/10 text-purple-600 border-purple-600/20"
                  >
                    {getPermissionName(permissionId)}
                    <button
                      onClick={() =>
                        handlePermissionChange(permissionId, false)
                      }
                      className="ml-1 hover:bg-purple-600/20 rounded-full p-0.5"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <Label>Available Permissions</Label>
              {isError ? (
                <p>There was an error fetching permissions</p>
              ) : (
                <div>
                  {isLoading ? (
                    <div className="flex place-content-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto">
                      {permissions.data.map((permission) => (
                        <div
                          key={permission.id}
                          className="flex items-start space-x-3 p-3 border rounded-lg"
                        >
                          <Checkbox
                            id={`edit-${permission.id}`}
                            checked={formData.permissions.includes(
                              permission.id
                            )}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(permission.id, checked)
                            }
                          />
                          <div className="space-y-1">
                            <Label
                              htmlFor={`edit-${permission.id}`}
                              className="font-medium cursor-pointer"
                            >
                              {permission.name}
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              {permission.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRole}
              disabled={(() => {
                if (selectedRole?.name === selectedRole?.baseRole) {
                  return true;
                } else if (!formData.name.trim()) {
                  return true;
                } else {
                  return false;
                }
              })()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete the role "{selectedRole?.name}"?
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
